import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  ActionsSectionCol,
  ActionsWrapper,
  ActionButton
} from './ActionsSection.styles'
import { useTranslation } from 'react-i18next'
import ReleasePlaylistDropdown from 'components/Dropdowns/PlaylistDropdown'
import { FlagHandlerProvider, ArchiveHandlerProvider } from 'components'
import { SidebarNavItem } from '@dsny/dsny-component-library'
import ReleaseActions from 'reducers/Releases'
import ContactModal from '../Modals/ContactModal/ContactModal'
import { ReleasesService } from 'services'
import { trackEvent } from 'utils/segmentService'
import { normalizeValue } from 'utils/analytics'
import useReleaseInformationResponsiveBreakpointsV2 from '../../hooks/useReleaseInformationResponsiveBreakpointsV2'

const ActionsSectionLegacy = ({
  release,
  setRelease,
  alignWithDetails = false
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()
  const { currentReleaseInformation } = useSelector((state) => state.releases)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)

  const setFlagOnRelease = (flag) => {
    const updatedRelease = {
      ...release,
      flagged: flag,
      songs: release.songs?.map((song) => ({
        ...song,
        flagged: flag
      }))
    }

    setRelease(updatedRelease)
    dispatch(ReleaseActions.setCurrentReleaseInformation(updatedRelease))
    dispatch(ReleaseActions.updateRelease(updatedRelease))

    trackEvent('Release Flag', {
      release_id: normalizeValue(release?.releaseid),
      artist: normalizeValue(release?.artist),
      title: normalizeValue(release?.title),
      action: flag ? 'flag' : 'unflag',
      source: 'release_information',
      type: 'release_flag'
    })
  }

  const setArchiveOnRelease = (archived) => {
    const shouldUnflagOnArchive = archived && release?.flagged
    const updatedRelease = {
      ...release,
      archived,
      flagged: shouldUnflagOnArchive ? false : release.flagged,
      songs: shouldUnflagOnArchive
        ? release.songs?.map((song) => ({
            ...song,
            flagged: false
          }))
        : release.songs
    }

    setRelease(updatedRelease)
    dispatch(ReleaseActions.setCurrentReleaseInformation(updatedRelease))
    dispatch(ReleaseActions.updateRelease(updatedRelease))

    trackEvent('Release Archive', {
      release_id: normalizeValue(release?.releaseid),
      artist: normalizeValue(release?.artist),
      title: normalizeValue(release?.title),
      action: archived ? 'archive' : 'unarchive',
      unflagged_on_archive: normalizeValue(archived && release?.flagged),
      source: 'release_information',
      type: 'release_archive'
    })
  }

  const openContactModal = () => {
    setIsContactModalOpen(true)

    try {
      ReleasesService.trackCommentingInit(currentReleaseInformation?.releaseid)
    } catch (error) {
      console.log('Track error', error)
    }
    trackEvent('Release Contact', {
      release_id: normalizeValue(currentReleaseInformation?.releaseid),
      artist: normalizeValue(currentReleaseInformation?.artist),
      title: normalizeValue(currentReleaseInformation?.title),
      source: 'release_information_actions_section',
      type: 'contact'
    })
  }

  if (!currentReleaseInformation) {
    return null
  }

  return (
    <>
      <ActionsSectionCol
        $alignWithDetails={alignWithDetails}
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
      >
        <ActionsWrapper $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          <ActionButton $responsiveBreakpointsV2={responsiveBreakpointsV2}>
            {currentReleaseInformation?.commenting_enabled && (
              <SidebarNavItem
                as="button"
                onClick={openContactModal}
                label={t('release-info.contact')}
                iconName="Mail"
                setting="player"
                mode="mobile"
                isBackgroundTransparent="true"
              />
            )}
            <ReleasePlaylistDropdown />

            <FlagHandlerProvider
              release={release}
              callback={setFlagOnRelease}
              render={({ handleFlag }) => {
                return (
                  <SidebarNavItem
                    as="button"
                    onClick={handleFlag}
                    label={
                      release?.flagged ? t('general.unflag') : t('general.flag')
                    }
                    iconName="Flag"
                    isActive={release?.flagged}
                    setting="player"
                    mode="mobile"
                    isBackgroundTransparent="true"
                  />
                )
              }}
            />
            <ArchiveHandlerProvider
              release={release}
              callback={setArchiveOnRelease}
              render={({ handleArchive }) => {
                return (
                  <SidebarNavItem
                    as="button"
                    onClick={handleArchive}
                    label={
                      release?.archived
                        ? t('general.unarchive')
                        : t('general.archive')
                    }
                    iconName="Archive"
                    isActive={release?.archived}
                    setting="player"
                    mode="mobile"
                    isBackgroundTransparent="true"
                  />
                )
              }}
            />
          </ActionButton>
        </ActionsWrapper>
      </ActionsSectionCol>
      <ContactModal
        isContactModalOpen={isContactModalOpen}
        setIsContactModalOpen={setIsContactModalOpen}
      />
    </>
  )
}

export default ActionsSectionLegacy
